import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UploadSimple, ArrowLeft, FileText, CheckCircle, Clock, Warning, Buildings,
  Star, PushPin, GitBranch, XCircle, MagnifyingGlass,
} from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    rejected: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${map[status]}`}>
      {status}
    </span>
  );
}

const EVENT_ICONS = {
  upload: { icon: UploadSimple, color: "text-cyan", label: "Uploaded" },
  version: { icon: GitBranch, color: "text-blue-500", label: "New version" },
  approve: { icon: CheckCircle, color: "text-emerald-500", label: "Approved" },
  reject: { icon: XCircle, color: "text-red-500", label: "Rejected" },
};

function ActivityRow({ evt }) {
  const spec = EVENT_ICONS[evt.type] || EVENT_ICONS.upload;
  const Icon = spec.icon;
  return (
    <Link
      to={`/documents/${evt.doc_id}`}
      data-testid={`activity-${evt.doc_id}-${evt.type}-${evt.at || ""}`}
      className="flex items-start gap-3 p-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
    >
      <div className={`w-8 h-8 shrink-0 rounded-sm bg-slate-100 flex items-center justify-center ${spec.color}`}>
        <Icon size={14} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500">
          <span className="text-navy font-semibold">{evt.actor}</span> · {spec.label} <span className="font-mono text-slate-400">{evt.note}</span>
        </div>
        <div className="text-sm font-medium text-navy truncate">{evt.doc_title}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
          {evt.at ? new Date(evt.at).toLocaleString() : "—"}
        </div>
      </div>
    </Link>
  );
}

export default function DepartmentWorkspace() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [dep, setDep] = useState(null);
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinning, setPinning] = useState(false);
  const [q, setQ] = useState("");

  const isPinned = (user?.pinned_department_ids || []).includes(id);
  const canUpload = user?.role === "admin" || user?.role === "manager" ||
    (user?.role === "operator" && (user?.department_ids || []).includes(id));

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [deps, c, docsRes, actRes] = await Promise.all([
          api.get("/departments"),
          api.get("/categories"),
          api.get(`/documents?department_id=${id}`),
          api.get(`/departments/${id}/activity?limit=15`),
        ]);
        setDep(deps.data.find((d) => d.id === id) || null);
        setCats(c.data);
        setDocs(docsRes.data);
        setEvents(actRes.data.events || []);
      } catch (err) {
        // Activity endpoint fails silently for non-existent dept — caller shows "not found"
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const togglePin = async () => {
    setPinning(true);
    try {
      const { data } = await api.post("/users/me/pinned-departments/toggle", { department_id: id });
      // Optimistically update user in context if setUser is available
      if (setUser) setUser({ ...user, pinned_department_ids: data.pinned_department_ids });
      toast.success(data.pinned ? "Pinned to your Dashboard" : "Removed from your Dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setPinning(false);
    }
  };

  const catName = (cid) => cats.find((c) => c.id === cid)?.name || "—";

  const filteredDocs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter((d) => {
      const hay = [
        d.title || "",
        d.description || "",
        (d.tags || []).join(" "),
        catName(d.category_id),
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [docs, q, cats]);

  const stats = useMemo(() => {
    const approved = docs.filter((d) => d.status === "approved").length;
    const pending = docs.filter((d) => d.status === "pending").length;
    const expiring = docs.filter((d) => {
      if (!d.expiry_date) return false;
      const diff = (new Date(d.expiry_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff <= 30;
    }).length;
    return { total: docs.length, approved, pending, expiring };
  }, [docs]);

  if (loading) {
    return <div className="text-slate-400 text-sm">Loading department…</div>;
  }
  if (!dep) {
    return (
      <div className="card-flat p-8 text-center">
        <div className="text-navy font-semibold">Department not found</div>
        <Link to="/documents" className="text-cyan text-sm mt-2 inline-block">← Back to library</Link>
      </div>
    );
  }

  const initial = dep.name.trim().charAt(0).toUpperCase();

  return (
    <div className="space-y-6" data-testid={`dept-workspace-${id}`}>
      <Link
        to="/documents"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 hover:text-navy"
      >
        <ArrowLeft size={12} /> All departments
      </Link>

      {/* Header banner */}
      <div className="card-flat overflow-hidden">
        <div className="bg-navy text-white p-6 flex items-start gap-5">
          <div className="w-16 h-16 shrink-0 rounded-sm bg-cyan text-navy flex items-center justify-center font-display text-3xl">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-cyan flex items-center gap-1.5">
              <Buildings size={11} weight="fill" /> Department workspace
            </div>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="font-display text-3xl uppercase leading-tight">{dep.name}</h1>
              <button
                onClick={togglePin}
                disabled={pinning}
                data-testid="dept-pin-btn"
                title={isPinned ? "Unpin from Dashboard" : "Pin to Dashboard"}
                className={`w-8 h-8 flex items-center justify-center rounded-sm transition-colors disabled:opacity-50 ${
                  isPinned ? "bg-cyan text-navy" : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-cyan"
                }`}
              >
                {isPinned ? <PushPin size={14} weight="fill" /> : <Star size={14} weight="regular" />}
              </button>
            </div>
            {dep.description && <p className="text-white/70 text-sm mt-2 max-w-2xl">{dep.description}</p>}
          </div>
          {canUpload && (
            <Link
              to={`/upload?dept=${dep.id}`}
              data-testid="dept-upload-cta"
              className="btn-cyan flex items-center gap-2 shrink-0"
            >
              <UploadSimple size={14} /> Upload to {dep.name.split(" ")[0]}
            </Link>
          )}
        </div>
        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200 border-t border-slate-200 bg-white">
          <StatCell label="Documents" value={stats.total} icon={<FileText size={14} className="text-cyan" weight="fill" />} />
          <StatCell label="Approved" value={stats.approved} icon={<CheckCircle size={14} className="text-emerald-500" weight="fill" />} />
          <StatCell label="Pending" value={stats.pending} icon={<Clock size={14} className="text-amber-500" weight="fill" />} />
          <StatCell label="Expiring 30d" value={stats.expiring} icon={<Warning size={14} className="text-red-500" weight="fill" />} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Docs list */}
        <div className="lg:col-span-2 space-y-4">
          {docs.length > 0 && (
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="dept-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search inside ${dep.name}…`}
                className="w-full pl-9 pr-3 h-10 bg-white border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  data-testid="dept-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy text-xs"
                >
                  Clear · {filteredDocs.length}/{docs.length}
                </button>
              )}
            </div>
          )}

          {docs.length === 0 ? (
            <div className="card-flat p-14 text-center">
              <div className="w-14 h-14 rounded-sm bg-slate-100 flex items-center justify-center text-navy font-display text-2xl mx-auto">
                {initial}
              </div>
              <div className="text-navy font-semibold mt-4">No documents in {dep.name} yet</div>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                {canUpload
                  ? "Kick off the department library by uploading your first SOP, work instruction or quality record."
                  : "You don't currently have upload access to this department. Ask an admin to grant it."}
              </p>
              {canUpload && (
                <Link
                  to={`/upload?dept=${dep.id}`}
                  data-testid="dept-empty-upload-cta"
                  className="mt-5 inline-flex btn-navy items-center gap-2"
                >
                  <UploadSimple size={14} /> Upload first document
                </Link>
              )}
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="card-flat p-10 text-center text-sm text-slate-500">
              No documents in {dep.name} match &quot;{q}&quot;.
            </div>
          ) : (
            <div className="card-flat overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-zinc-200 bg-zinc-50">
                    <th className="px-4 py-3 label-caps">Title</th>
                    <th className="px-4 py-3 label-caps">Category</th>
                    <th className="px-4 py-3 label-caps">Version</th>
                    <th className="px-4 py-3 label-caps">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((d) => (
                    <tr key={d.id} className="dms-row border-b border-zinc-100 last:border-b-0" data-testid={`dept-doc-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/documents/${d.id}`} className="font-semibold text-navy hover:text-cyan transition-colors">
                          {d.title}
                        </Link>
                        {d.tags?.length > 0 && (
                          <div className="text-xs text-zinc-500 mt-0.5">{d.tags.join(" · ")}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{catName(d.category_id)}</td>
                      <td className="px-4 py-3 font-mono text-zinc-700">v{d.current_version}</td>
                      <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="card-flat" data-testid="activity-feed">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="label-caps">Recent activity</div>
            <div className="text-xs text-slate-500 mt-0.5">Uploads, approvals & version bumps</div>
          </div>
          {events.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Clock size={20} className="mx-auto mb-2 text-slate-300" />
              No activity yet.
            </div>
          ) : (
            <div>
              {events.map((e, i) => <ActivityRow key={`${e.type}-${e.doc_id}-${e.at || i}`} evt={e} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, icon }) {
  return (
    <div className="px-4 py-3">
      <div className="label-caps mute flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display text-2xl text-navy mt-0.5">{value}</div>
    </div>
  );
}
